import type { PropsWithChildren } from "react";
import { t } from "@/i18n-js/instance";
import type { AnalyticsTableColumn } from "@/react/components/Analytics/components/AnalyticsTableComponent";
import type {
  ChartDataItem,
  MappedBreakdownItem,
} from "@/react/components/Analytics/components/types";
import { classNames } from "@/react/helpers/twMergeWithCN";
import type { ChartType } from "@/react/hooks/analytics/chartTypes";
import { useAnalyticsCSVDownload } from "@/react/hooks/analytics/useAnalyticsCSVDownload";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";

interface ChartContainerProps<T = Record<string, unknown>> {
  chartType?: ChartType;
  children: React.ReactNode;
  className?: string;
  columns?: AnalyticsTableColumn<T>[];
  data?: ChartDataItem[] | MappedBreakdownItem[] | T[];
  prefix?: string;
  seriesKeys?: string[];
  suffix?: string;
  title: string;
  tooltip?: string;
}

export const ChartContainer = <T extends Record<string, unknown>>({
  title,
  tooltip,
  children,
  className,
  data,
  chartType,
  prefix,
  suffix,
  seriesKeys,
  columns,
}: PropsWithChildren<ChartContainerProps<T>>) => {
  const { downloadCSV } = useAnalyticsCSVDownload();

  const handleDownloadCSV = () => {
    if (data && chartType) {
      downloadCSV(data as any, chartType, title, {
        prefix,
        suffix,
        seriesKeys,
        columns,
      });
    }
  };

  const showDownloadButton = data && chartType;
  const rootLocale = "settings.analytics.export_csv";

  return (
    <div
      className={classNames(
        "border-primary flex w-full flex-col gap-2 rounded-xl border p-6",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <Typography.LabelMd
          color="text-primary dark:text-white"
          weight="medium"
        >
          {title}
        </Typography.LabelMd>
        <div className="flex items-center gap-1">
          {tooltip && (
            <TippyV2
              content={tooltip}
              className="inline-flex cursor-pointer dark:text-white"
            >
              <Icon type="16-info" size={16} useWithStrokeCurrentColor />
            </TippyV2>
          )}
          {showDownloadButton && (
            <Dropdown
              direction="bottom-end"
              menuWrapperClassName="!mt-2"
              buttonClassName="flex items-center h-6 w-6 flex justify-center !rounded transition-colors duration-200 hover:bg-tertiary focus-visible:outline-secondary focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:bg-tertiary"
              button={
                <TippyV2
                  content={t([rootLocale, "download_options"])}
                  className="flex items-center p-1"
                >
                  <Icon
                    className="text-default !h-4 !w-4"
                    type="20-menu-dots-horizontal"
                    size={16}
                    aria-label={t([rootLocale, "download_options"])}
                  />
                </TippyV2>
              }
              options={[
                {
                  label: t([rootLocale, "download_csv"]),
                  value: "download_csv",
                  icon: "download",
                  onClick: handleDownloadCSV,
                },
              ]}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
};
